import { strings } from './strings';
import type { UniverseAssetType } from './types';

export function typeLabel(type: UniverseAssetType): string {
  return strings.types[type] ?? strings.types.document;
}

export function formatMonthYear(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/** 0:42 · 12:07 · 1:04:09 — the iOS media duration format. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** 840 KB · 12.4 MB — decimal units, as Finder and Files show them. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} bytes`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Deterministic, premium-looking fallback when an asset has no artwork. */
export function fallbackGradient(seed: string): string {
  const h = hashString(seed || 'universe');
  const hue = h % 360;
  const hue2 = (hue + 28 + (h % 24)) % 360;
  return `radial-gradient(120% 120% at 30% 20%, hsl(${hue} 55% 86%) 0%, hsl(${hue2} 45% 70%) 55%, hsl(${hue2} 40% 46%) 100%)`;
}

export function initials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return 'U';
  const second = words[1];
  if (!second) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}
